import { Calendar } from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'

export default function initializeDatePicker() {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            initializeDateInputs()
            initializeTimePickers()
        })
    } else {
        initializeDateInputs()
        initializeTimePickers()
    }
}

function initializeDateInputs() {
    const dateInputs = document.querySelectorAll('#datePicker')

    if (dateInputs.length === 0) {
        console.warn('No date picker inputs found with id #datePicker')
        return
    }

    dateInputs.forEach(input => {
        let calendar = null
        let selectedDate = null

        // Hide the input field
        input.style.display = 'none'

        // Get parent container
        const parent = input.parentNode

        // Check if calendar container already exists
        let calendarContainer = parent.querySelector('.date-picker-calendar')

        // If calendar already exists, remove it to prevent duplicates
        if (calendarContainer) {
            console.log('Removing existing calendar to prevent duplication')
            calendarContainer.remove()
        }

        // Create new calendar container
        calendarContainer = document.createElement('div')
        calendarContainer.className = 'date-picker-calendar visible'

        // Ensure parent has proper positioning
        parent.style.position = 'relative'
        parent.insertBefore(calendarContainer, input)

        // Initialize calendar
        calendar = new Calendar(calendarContainer, {
            plugins: [dayGridPlugin, interactionPlugin],
            initialView: 'dayGridMonth',
            locale: 'en',
            direction: 'ltr',
            dayHeaderFormat: { weekday: 'short' },
            headerToolbar: {
                left: 'prev',
                center: 'title',
                right: 'next'
            },
            dateClick: function(info) {
                // Remove previous selection
                const allDays = calendarContainer.querySelectorAll('.fc-daygrid-day')
                allDays.forEach(day => day.classList.remove('fc-day-selected'))

                // Add selection to clicked day
                info.dayEl.classList.add('fc-day-selected')

                // Format the selected date
                const date = new Date(info.date)
                const year = date.getFullYear()
                const month = String(date.getMonth() + 1).padStart(2, '0')
                const day = String(date.getDate()).padStart(2, '0')
                const formattedDate = `${year}-${month}-${day}`

                // Update hidden input value
                input.value = formattedDate
                selectedDate = formattedDate

                // Trigger change event for form validation and other listeners
                input.dispatchEvent(new Event('change', { bubbles: true }))

                console.log('Date selected:', formattedDate)
            },
            datesSet: function() {
                // Highlight previously selected date when month changes
                if (selectedDate) {
                    const dateCell = calendarContainer.querySelector(`[data-date="${selectedDate}"]`)
                    if (dateCell) {
                        dateCell.classList.add('fc-day-selected')
                    }
                }
            }
        })

        // Render calendar immediately and keep it visible
        try {
            calendar.render()
            console.log('Calendar rendered successfully and visible by default')
        } catch (error) {
            console.error('Calendar render failed:', error)
            return
        }
    })
}

function initializeTimePickers() {
    const timePickers = document.querySelectorAll('.time-picker-input')

    if (timePickers.length === 0) {
        console.warn('No time picker inputs found with class .time-picker-input')
        return
    }

    timePickers.forEach(picker => {
        const valueElement = picker.querySelector('.time-picker-value')
        const wrapper = picker.closest('.time-picker-wrapper')

        if (!wrapper || !valueElement) {
            console.warn('Time picker structure incomplete')
            return
        }

        let dropdown = wrapper.querySelector('.time-picker-dropdown')

        // Check if dropdown already exists to prevent duplication
        if (dropdown) {
            console.log('Time picker dropdown already exists, skipping creation')
        } else {
            dropdown = createTimePickerDropdown()
            wrapper.style.position = 'relative'
            wrapper.appendChild(dropdown)

            if (!valueElement.textContent || valueElement.textContent === '') {
                valueElement.textContent = '12:00 PM'
            }
        }

        // Remove existing event listeners by cloning the element
        const newPicker = picker.cloneNode(true)
        picker.parentNode.replaceChild(newPicker, picker)

        // Re-query after replacement
        const updatedPicker = wrapper.querySelector('.time-picker-input')
        const updatedDropdown = wrapper.querySelector('.time-picker-dropdown')
        const updatedValueElement = updatedPicker.querySelector('.time-picker-value')

        updatedPicker.addEventListener('click', (e) => {
            e.stopPropagation()

            document.querySelectorAll('.time-picker-dropdown.active').forEach(d => {
                if (d !== updatedDropdown) {
                    d.classList.remove('active')
                }
            })

            updatedDropdown.classList.toggle('active')
        })

        updatedDropdown.addEventListener('click', (e) => {
            e.stopPropagation()

            if (e.target.classList.contains('time-option')) {
                const group = e.target.closest('.time-option-group')
                const options = group.querySelectorAll('.time-option')
                options.forEach(opt => opt.classList.remove('selected'))
                e.target.classList.add('selected')

                updateTimeValue(updatedDropdown, updatedValueElement)
            }
        })

        document.addEventListener('click', (e) => {
            if (!wrapper.contains(e.target)) {
                updatedDropdown.classList.remove('active')
            }
        })
    })
}

function createTimePickerDropdown() {
    const dropdown = document.createElement('div')
    dropdown.className = 'time-picker-dropdown'

    const hours = Array.from({ length: 12 }, (_, i) => i + 1)
    const minutes = Array.from({ length: 60 }, (_, i) => i).filter(m => m % 5 === 0)
    const periods = ['AM', 'PM']

    dropdown.innerHTML = `
        <div class="time-options">
            <div class="time-option-group" data-type="hour">
                <div class="time-option-label">Hour</div>
                <div class="time-option-scroll">
                    ${hours.map(h => `<div class="time-option" data-value="${h}">${h}</div>`).join('')}
                </div>
            </div>
            <div class="time-option-group" data-type="minute">
                <div class="time-option-label">Minute</div>
                <div class="time-option-scroll">
                    ${minutes.map(m => `<div class="time-option" data-value="${m}">${String(m).padStart(2, '0')}</div>`).join('')}
                </div>
            </div>
            <div class="time-option-group" data-type="period">
                <div class="time-option-label">Period</div>
                <div class="time-option-scroll">
                    ${periods.map(p => `<div class="time-option" data-value="${p}">${p}</div>`).join('')}
                </div>
            </div>
        </div>
    `

    return dropdown
}

function updateTimeValue(dropdown, valueElement) {
    const hourElement = dropdown.querySelector('[data-type="hour"] .time-option.selected')
    const minuteElement = dropdown.querySelector('[data-type="minute"] .time-option.selected')
    const periodElement = dropdown.querySelector('[data-type="period"] .time-option.selected')

    if (hourElement && minuteElement && periodElement) {
        const hour = hourElement.dataset.value
        const minute = minuteElement.dataset.value
        const period = periodElement.dataset.value

        valueElement.textContent = `${hour}:${String(minute).padStart(2, '0')} ${period}`
        valueElement.dispatchEvent(new Event('change', { bubbles: true }))
    }
}
